import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Polyline } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './BookingSummary.css';
import { useLanguage } from './i18n/LanguageContext';

const mapDot = (color) => L.divIcon({
  className: 'summary-map-dot',
  html: `<span style="background:${color}"></span>`,
  iconSize: [16, 16],
  iconAnchor: [8, 8]
});

const formatRupee = (amount) => `₹ ${Number(amount || 0).toLocaleString('en-IN')}`;

const BookingSummary = ({ tripData, selectedVehicle, paymentMethod, onBack, onProceedToPayment, onEditPaymentMethod, onSelectPaymentMethod }) => {
  const { t } = useLanguage();
  const [isWide, setIsWide] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(min-width: 768px)').matches
  );

  useEffect(() => {
    const media = window.matchMedia('(min-width: 768px)');
    const onChange = (event) => setIsWide(event.matches);
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);
  // Extract data from props
  const packageName = selectedVehicle?.packageName || '';
  const carMakeName = selectedVehicle?.carMakeName || '';
  const carModelName = selectedVehicle?.carModelName || '';
  const vehicleName = `${carMakeName} ${carModelName}`.trim() || selectedVehicle?.categoryName || selectedVehicle?.name || 'Executive SUV';
  const vehicleImage = selectedVehicle?.imageUrl || selectedVehicle?.image || null;
  
  const pickupLocation = tripData?.pickupPoint || 'Hotel Radisson Blu';
  const dropLocation = tripData?.dropDestination || 'Indira Gandhi International';
  
  // Extract vehicle data - fare is already calculated for round trip in API
  const basePrice = selectedVehicle?.basePrice || 0;
  const estimatedTotalCost = selectedVehicle?.estimatedTotalCost || 0;
  const fare = estimatedTotalCost || basePrice || 0;

  // The charged total stays the API fare. The rows split that total for display.
  const grandTotal = Number(fare) || 0;
  const baseFare = Math.round(grandTotal / 1.1);
  const gstAmount = Math.round(baseFare * 0.05);
  const serviceFee = grandTotal - baseFare - gstAmount;
  const onlineSelected = paymentMethod === 'upi' || paymentMethod === 'card';
  const cashSelected = paymentMethod === 'cash';
  const wideReady = onlineSelected || cashSelected;
  const pickupLat = tripData?.pickupLocation?.latitude;
  const pickupLng = tripData?.pickupLocation?.longitude;
  const dropLat = tripData?.dropLocation?.latitude;
  const dropLng = tripData?.dropLocation?.longitude;
  const hasRoute = [pickupLat, pickupLng, dropLat, dropLng].every((value) => typeof value === 'number');

  return (
    <div className={`booking-summary-screen ${isWide ? 'is-wide' : ''}`}>
      {isWide && hasRoute && (
        <div className="summary-map">
          <MapContainer
            center={[(pickupLat + dropLat) / 2, (pickupLng + dropLng) / 2]}
            zoom={12}
            style={{ height: '100%', width: '100%' }}
            scrollWheelZoom={false}
            zoomControl={false}
          >
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            <Polyline positions={[[pickupLat, pickupLng], [dropLat, dropLng]]} pathOptions={{ color: '#764CA2', weight: 4 }} />
            <Marker position={[pickupLat, pickupLng]} icon={mapDot('#4CAF50')} />
            <Marker position={[dropLat, dropLng]} icon={mapDot('#FF9800')} />
          </MapContainer>
        </div>
      )}
      <div className="booking-summary-content">
        {/* Header */}
        <header className="summary-header">
          <button className="back-button" onClick={onBack} aria-label="Go back">
            <img
              src={process.env.PUBLIC_URL + '/images/back_button.svg'}
              alt="Back"
            />
          </button>
          <div className="summary-heading">
            <h1 className="summary-title">
              <span className="title-mobile">{t('Booking summary')}</span>
              <span className="title-wide">{t('Booking Summary')}</span>
            </h1>
            <p className="summary-subtitle">{t('Review your journey and fare.')}</p>
          </div>
        </header>

        {/* Booking Details Card */}
        <div className="booking-card">
          {/* Vehicle Section */}
          <div className="vehicle-section">
            <div className="vehicle-image-container">
              {vehicleImage ? (
                <img
                  src={vehicleImage}
                  alt={vehicleName}
                  className="vehicle-thumbnail"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'block';
                  }}
                />
              ) : null}
              <img
                src={process.env.PUBLIC_URL + '/images/car.svg'}
                alt=""
                className="vehicle-thumbnail vehicle-thumbnail-fallback"
                style={{ display: vehicleImage ? 'none' : 'block' }}
              />
            </div>
            <div className="vehicle-info">
              <h2 className="vehicle-name">{packageName || vehicleName}</h2>
              {packageName && vehicleName && (
                <p className="vehicle-series">{vehicleName}</p>
              )}
              {!packageName && (
                <p className="vehicle-series">{t('PREMIUM SERIES')}</p>
              )}
            </div>
          </div>

          {/* Location Section */}
          <div className="location-section">
            <div className="location-item">
              <div className="location-indicator-item pickup-indicator">
                <div className="location-dot-filled"></div>
              </div>
              <div className="location-details">
                <span className="location-label">{t('PICK UP')}</span>
                <span className="location-name">{pickupLocation}</span>
              </div>
            </div>
            <div className="location-item">
              <div className="location-indicator-item  drop-indicator">
                <div className="location-dot-outline"></div>
              </div>
              <div className="location-details">
                <span className="location-label">{t('DROP OFF')}</span>
                <span className="location-name">{dropLocation}</span>
              </div>
            </div>
          </div>

          {/* Fare Breakdown */}
          <div className="fare-section">
            <div className="fare-item">
              <span className="fare-label">{t('Base fare')}</span>
              <span className="fare-value">{formatRupee(baseFare)}</span>
            </div>
            <div className="fare-item">
              <span className="fare-label">{t('GST (5%)')}</span>
              <span className="fare-value">{formatRupee(gstAmount)}</span>
            </div>
            <div className="fare-item">
              <span className="fare-label">{t('Service fee')}</span>
              <span className="fare-value">{formatRupee(serviceFee)}</span>
            </div>
            <div className="fare-divider"></div>
            <div className="fare-item grand-total">
              <span className="fare-label">{t('Grand total')}</span>
              <span className="fare-value">{formatRupee(grandTotal)}</span>
            </div>
          </div>
          <p className="disclaimer">* Parking and toll charges will be extra</p>
        </div>

        {isWide && (
          <div className="summary-payments">
            <button
              type="button"
              className={`summary-pay-option ${onlineSelected ? 'selected' : ''}`}
              onClick={() => onSelectPaymentMethod && onSelectPaymentMethod('upi')}
            >
              <span className="summary-pay-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="2" y="5" width="20" height="14" rx="2" stroke="currentColor" strokeWidth="2"/>
                  <path d="M2 10H22" stroke="currentColor" strokeWidth="2"/>
                </svg>
              </span>
              <span className="summary-pay-copy">
                <strong>{t('Online Payment')}</strong>
                <small>{t('UPI, Wallet, Netbanking')}</small>
              </span>
              <span className="summary-pay-radio" />
            </button>
            <button
              type="button"
              className={`summary-pay-option ${cashSelected ? 'selected' : ''}`}
              onClick={() => onSelectPaymentMethod && onSelectPaymentMethod('cash')}
            >
              <span className="summary-pay-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="2" y="4" width="20" height="16" rx="2" stroke="currentColor" strokeWidth="2"/>
                  <path d="M2 8H22" stroke="currentColor" strokeWidth="2"/>
                  <circle cx="6" cy="14" r="1.5" fill="currentColor"/>
                  <circle cx="18" cy="14" r="1.5" fill="currentColor"/>
                </svg>
              </span>
              <span className="summary-pay-copy">
                <strong>{t('Cash to driver')}</strong>
                <small>{t('Pay after trip ends')}</small>
              </span>
              <span className="summary-pay-radio" />
            </button>
          </div>
        )}

        {/* Payment Method Card (if selected) */}
        {paymentMethod && (
          <div className="payment-method-card">
            <div className="payment-method-icon">
              {paymentMethod === 'upi' && (
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="3" y="3" width="7" height="7" stroke="currentColor" strokeWidth="2"/>
                  <rect x="14" y="3" width="7" height="7" stroke="currentColor" strokeWidth="2"/>
                  <rect x="3" y="14" width="7" height="7" stroke="currentColor" strokeWidth="2"/>
                  <rect x="14" y="14" width="7" height="7" stroke="currentColor" strokeWidth="2"/>
                </svg>
              )}
              {paymentMethod === 'card' && (
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="2" y="5" width="20" height="14" rx="2" stroke="currentColor" strokeWidth="2"/>
                  <path d="M2 10H22" stroke="currentColor" strokeWidth="2"/>
                </svg>
              )}
              {paymentMethod === 'cash' && (
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="2" y="4" width="20" height="16" rx="2" stroke="currentColor" strokeWidth="2"/>
                  <path d="M2 8H22" stroke="currentColor" strokeWidth="2"/>
                  <circle cx="6" cy="14" r="1.5" fill="currentColor"/>
                  <circle cx="18" cy="14" r="1.5" fill="currentColor"/>
                </svg>
              )}
            </div>
            <div className="payment-method-info">
              <span className="payment-method-label">{t('Payment method')}</span>
              <span className="payment-method-name">
                {paymentMethod === 'upi' && 'UPI (GPay, PhonePe)'}
                {paymentMethod === 'card' && 'Credit / Debit Card'}
                {paymentMethod === 'cash' && 'Cash to Driver'}
              </span>
            </div>
            <button className="edit-payment-btn" onClick={onEditPaymentMethod}>
              EDIT
            </button>
          </div>
        )}

        {/* Proceed to Payment / Submit Request Button */}
        <button
          className={`payment-btn ${isWide && !wideReady ? 'is-waiting' : ''}`}
          onClick={onProceedToPayment}
          disabled={isWide && !wideReady}
        >
          {isWide ? 'Confirm Payment Method' : (paymentMethod ? 'Submit Request' : 'Choose Payment Method')}
        </button>
      </div>
    </div>
  );
};

export default BookingSummary;
